import { useHomeAide, HomeAideHero, HomeAideProfile, HomeAideDailyActivity } from "@/components/family/HomeAide";
import ResidentPortrait from "@/components/ResidentPortrait";
import { PortraitEditor } from "@/components/PortraitPicker";
import { AVATARS } from "@/data/exercises";
import { GardiensGallery } from "@/pages/Gardiens";
import { useState, useEffect, useCallback } from "react";
import { GardienBadge } from "@/components/GardienBadge";
import { GardienPickerButton, GardienCardButton } from "@/components/GardienPicker";
import { getGardien, gardienLabel } from "@/data/gardiens";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/data/exercises";
import GamesBrowser from "@/components/patient/GamesBrowser";
import FamilyVoices from "@/components/FamilyVoices";
import TeamMessages from "@/components/family/TeamMessages";
import FavoriteSongsPicker from "@/components/music/FavoriteSongsPicker";
import FamilyPhotos, { useFamilyPhotos, FamilyPhotosBadge } from "@/components/family/FamilyPhotos";
import VoiceBookletButton from "@/components/VoiceBookletButton";
import FamilyInfo from "@/components/family/FamilyInfo";
import { UpsellBanner } from "@/components/family/UpsellBanner";
import BookletButton from "@/components/BookletButton";
import { FamiliarAlbumGrid } from "@/components/patient/FamiliarAlbum";
import { Button } from "@/components/ui/button";
import {
  Heart, Smile, LogOut, LayoutGrid, MessageSquare, Gamepad2, Home, Sparkles, Loader2, Quote, CalendarDays, CreditCard, BookOpen, Feather, FileDown } from "lucide-react";

const FEELINGS = ["Rassuré(e)", "Ému(e)", "Heureux(se)", "Reconnaissant(e)"];

function UpdateCard({ u, onReact }) {
  const [reaction, setReaction] = useState(u.my_reaction || "");
  const [feeling, setFeeling] = useState(u.my_feeling || "");
  const [saved, setSaved] = useState(Boolean(u.my_reaction || u.my_feeling));

  const send = (r, f) => {
    setReaction(r); setFeeling(f);
    onReact(u.id, r, f);
    setSaved(true);
  };

  return (
    <div className="rounded-3xl bg-white border-4 border-emerald-700/10 p-6 shadow-sm" data-testid={`family-update-${u.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </span>
        <div>
          <p className="text-lg text-gray-800 font-patient">{u.message}</p>
          <p className="text-xs text-gray-400 mt-1">{u.created_at?.slice(0, 10)}</p>
        </div>
      </div>

      <div className="border-t pt-4">
        <p className="font-patient font-bold text-gray-600 mb-3">Comment vous sentez-vous après avoir lu ces nouvelles ?</p>
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => send("coeur", feeling)} data-testid={`react-heart-${u.id}`}
            className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-colors ${reaction === "coeur" ? "bg-rose-500 border-rose-500 text-white" : "bg-white border-rose-200 text-rose-400"}`}>
            <Heart className="w-6 h-6" />
          </button>
          <button onClick={() => send("sourire", feeling)} data-testid={`react-smile-${u.id}`}
            className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-colors ${reaction === "sourire" ? "bg-amber-400 border-amber-400 text-white" : "bg-white border-amber-200 text-amber-500"}`}>
            <Smile className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {FEELINGS.map((f) => (
            <button key={f} onClick={() => send(reaction || "coeur", f)} data-testid={`feeling-${u.id}-${f}`}
              className={`px-4 h-10 rounded-full font-patient font-semibold border-2 transition-colors ${feeling === f ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-gray-200 text-gray-600"}`}>
              {f}
            </button>
          ))}
        </div>
        {saved && <p className="text-sm text-emerald-600 mt-3 font-semibold" data-testid={`react-thanks-${u.id}`}>Merci d'avoir partagé votre ressenti 💚</p>}
      </div>
    </div>
  );
}

export function FamilyRelativeView({ residence = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("nouvelles");
  const [photos, setPhotos] = useFamilyPhotos();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/family/overview");
      setData(data);
    } catch {
      setData({ patient: { name: "votre proche" }, updates: [], total_activities: 0 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const react = async (updateId, reaction, feeling) => {
    try {
      await api.post(`/family/updates/${updateId}/reaction`, { reaction, feeling });
    } catch {
      toast.error("Enregistrement impossible.");
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>;

  return (
    <div>
      <div className="rounded-[2rem] bg-white border border-amber-100 px-6 py-8 mb-8 text-center" data-testid="family-hero">
        <ResidentPortrait name={data.patient.name} avatar={data.patient.avatar} gardienId={data.patient.gardien_id} className="mb-6" testId="family-patient-portrait" />
        <h1 className="font-serif-display text-3xl font-normal text-gray-800">Des nouvelles de {data.patient.name}</h1>
        <p className="text-gray-500">{data.total_activities} activité(s) partagée(s) au total</p>
      </div>
      <FamilyPhotosBadge photos={photos} />

      <GardienBadge id={data.patient.gardien_id || "rose-auguste"}
        eyebrow={data.patient.gardien_id ? `${gardienLabel(getGardien(data.patient.gardien_id))} de ${data.patient.name}` : "Gardiens du lien familial"} className="mb-4" testId="family-gardien" />
      {residence ? (
        <div className="flex flex-wrap gap-3 mb-6">
          <ReportButton name={data.patient.name} />
          {data.patient.gardien_id && <GardienCardButton url="/family/gardien-card/pdf" fileName={`carte-gardien-${data.patient.name}.pdf`} testId="family-gardien-card" />}
        </div>
      ) : (
      <div className="flex flex-wrap gap-3 mb-6">
        <GardienPickerButton personName={data.patient.name} current={data.patient.gardien_id} testId="family-gardien-button"
          onSave={async (gid) => { await api.put("/family/gardien", { gardien_id: gid }); load(); }} />
        {data.patient.gardien_id && <GardienCardButton url="/family/gardien-card/pdf" fileName={`carte-gardien-${data.patient.name}.pdf`} testId="family-gardien-card" />}
      </div>
      )}
      <FamilyInfo data={data} />
      <FamilyPhotos patientName={data.patient.name} photos={photos} setPhotos={setPhotos} />
      <div className="rounded-3xl bg-white border border-border/60 p-6 mb-8" data-testid="family-familiars">
        <h3 className="font-serif-display text-2xl mb-1">L'album des familiers de {data.patient.name}</h3>
        <p className="text-gray-500 mb-4">Un nouveau petit compagnon le ou la rejoint toutes les 5 activités.</p>
        <FamiliarAlbumGrid url="/family/familiars" />
      </div>
      <FamilyVoices patientName={data.patient.name} defaultName={user?.name?.split(" ")[0]} />
      <div className="flex justify-center mb-8"><VoiceBookletButton url="/family/voice-booklet/pdf" name={data.patient.name} testId="family-voice-booklet" /></div>
      <FavoriteSongsPicker getUrl="/family/favorite-tracks" putUrl="/family/favorite-tracks" name={data.patient.name} />
      <TeamMessages patientName={data.patient.name} />
      <div className="rounded-3xl bg-amber-50 border-2 border-amber-200 p-6 mb-8 flex items-start gap-4" data-testid="family-reassurance-box">
        <span className="shrink-0 w-12 h-12 rounded-2xl bg-white text-rose-400 flex items-center justify-center border border-amber-100">
          <Heart className="w-6 h-6" />
        </span>
        <div>
          <p className="font-patient font-bold text-amber-900 mb-1">En toute bienveillance</p>
          <p className="font-care text-amber-800 leading-relaxed">
            Les jeux et exercices proposés ne sont pas quotidiens ni obligatoires. Nous respectons les
            envies, les humeurs et le rythme de chaque résident. Aucune activité n'est imposée&nbsp;:
            la participation est toujours libre et bienveillante.
          </p>
        </div>
      </div>

      {data.dicton_du_jour && (
        <div className="rounded-3xl bg-emerald-700 text-white p-6 mb-8" data-testid="family-dicton">
          <div className="flex items-center gap-2 text-emerald-100 font-semibold mb-1"><Quote className="w-5 h-5" /> Le dicton du jour</div>
          <p className="text-xl font-patient font-bold italic">« {data.dicton_du_jour} »</p>
          <p className="text-emerald-100 text-sm mt-2">Une petite phrase pour lancer la conversation aujourd'hui.</p>
        </div>
      )}

      {residence ? (
        <h2 className="font-serif-display text-3xl mb-6" data-testid="residence-news-title">Les nouvelles de l'équipe</h2>
      ) : (
      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setTab("nouvelles")} data-testid="family-tab-news"
          className={`px-5 h-11 rounded-full font-semibold ${tab === "nouvelles" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>Nouvelles</button>
        <button onClick={() => setTab("jeux")} data-testid="family-tab-games"
          className={`px-5 h-11 rounded-full font-semibold ${tab === "jeux" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>Jeux & souvenirs</button>
        <button onClick={() => setTab("gardiens")} data-testid="family-tab-gardiens"
          className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "gardiens" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}><Feather className="w-4 h-4" /> Les Gardiens</button>
        <button onClick={() => setTab("profil")} data-testid="family-tab-profile"
          className={`pl-1.5 pr-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "profil" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}><img src={data.patient.avatar || AVATARS[0]} alt="" className="w-8 h-8 rounded-full object-cover object-top" /> Profil de {data.patient.name}</button>
      </div>
      )}

      {tab === "nouvelles" ? (
        data.updates.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="family-no-news">
            <Quote className="w-10 h-10 mx-auto mb-3 text-emerald-200" />
            Les nouvelles partagées par l'équipe soignante apparaîtront ici.
          </div>
        ) : (
          <div className="grid gap-6 max-w-2xl">
            {data.updates.map((u) => <UpdateCard key={u.id} u={u} onReact={react} />)}
          </div>
        )
      ) : tab === "gardiens" ? (
        <GardiensGallery embedded />
      ) : tab === "profil" ? (
        <section className="rounded-3xl bg-white border border-border/60 p-6 sm:p-8" data-testid="family-profile">
          <h2 className="font-serif-display text-3xl mb-1">Le portrait de {data.patient.name}</h2>
          <p className="text-gray-500 mb-6">Choisissez le portrait, dans l'univers des Gardiens, qui représentera {data.patient.name} dans l'application.</p>
          <PortraitEditor personName={data.patient.name} current={data.patient.avatar}
            onSave={async (a) => { await api.put("/family/avatar", { avatar: a }); await load(); }} />
        </section>
      ) : (
        <GamesBrowser gardienId={data.patient.gardien_id} />
      )}
    </div>
  );
}

function CaregiverFamilyView() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("accueil");
  const home = useHomeAide();
  return (
    <div>
      <HomeAideHero aide={home.aide} canPatientMode={home.paid} />
      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setTab("accueil")} data-testid="famille-tab-accueil"
          className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "accueil" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}><Home className="w-4 h-4" /> Accueil</button>
        <button onClick={() => setTab("jeux")} data-testid="famille-tab-jeux"
          className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "jeux" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}><Gamepad2 className="w-4 h-4" /> Jeux & souvenirs</button>
        <button onClick={() => setTab("gardiens")} data-testid="famille-tab-gardiens"
          className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "gardiens" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}><Feather className="w-4 h-4" /> Les Gardiens</button>
        <button onClick={() => setTab("profil")} data-testid="famille-tab-profile"
          className={`pl-1.5 pr-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "profil" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
          <img src={home.aide?.avatar || AVATARS[0]} alt="" className="w-8 h-8 rounded-full object-cover object-top" /> {home.aide ? `Profil de ${home.aide.name}` : "Profil de mon proche"}</button>
        <button onClick={() => navigate("/temoignages")} data-testid="famille-tab-temoignages"
          className="px-5 h-11 rounded-full font-semibold flex items-center gap-2 bg-white border border-gray-200 text-gray-600"><MessageSquare className="w-4 h-4" /> Témoignages</button>
      </div>

      {tab === "gardiens" ? (
        <GardiensGallery embedded />
      ) : tab === "profil" ? (
        home.patients ? <HomeAideProfile {...home} /> : <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />
      ) : tab === "accueil" ? (
        <>
        {home.paid === false && <UpsellBanner aideName={home.aide?.name} />}
        <HomeAideDailyActivity aide={home.aide} />
        {home.aide && (
          <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="family-booklet-row">
            <BookletButton patient={home.aide} testId="family-booklet-button" />
            <span className="text-sm text-gray-500">Une semaine de jeux sur papier, à imprimer pour {home.aide.name}.</span>
          </div>
        )}
        <section className="rounded-3xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 to-amber-50" data-testid="plaisir-section">
          <div className="grid md:grid-cols-2 items-center">
            <div className="p-8 sm:p-10">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700 bg-white/70 px-3 py-1 rounded-full mb-4">
                <Heart className="w-4 h-4" /> Notre philosophie
              </span>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-normal text-gray-800 mb-4">Le Plaisir de se Retrouver</h2>
              <p className="text-gray-600 leading-relaxed">
                L'accompagnement cognitif est précieux pour aider à ralentir l'évolution des pathologies.
                Mais avant tout, il doit rester un moment de joie et de partage, sans aucune pression.
                Notre objectif : transformer le soin en un instant ludique et tendre, aussi doux pour
                l'aidant que pour l'aidé.
              </p>
            </div>
            <img src="/gardiens/plaisir-se-retrouver.jpg" alt="Une grand-mère et sa petite-fille jouent ensemble près de la fenêtre" className="w-full h-full object-cover aspect-[3/2] md:aspect-auto max-h-80 md:max-h-full" data-testid="plaisir-image" />
          </div>
        </section>
        </>
      ) : (
        <GamesBrowser key={home.aide?.id || "none"} gardienId={home.aide?.gardien_id} patientId={home.aide?.id} />
      )}
    </div>
  );
}

function ReportButton({ name }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/family/report/pdf", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `suivi-${name}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Rapport indisponible pour le moment."); }
    finally { setBusy(false); }
  };
  return (
    <Button onClick={download} disabled={busy} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="family-report-button">
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Rapport de suivi (PDF)
    </Button>
  );
}

export default function FamilySpace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isFamily = user?.role === "family";
  if (user?.space === "residence") return <Navigate to="/nouvelles" replace />;

  return (
    <div className="min-h-screen bg-[#F4F0E8] font-care">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300 hidden sm:inline">·</span>
            <span className="text-gray-500 text-sm font-semibold hidden sm:inline whitespace-nowrap">Aidant & Famille</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 max-w-full">
            <Button variant="outline" size="sm" onClick={() => navigate("/ephemeride")} className="rounded-lg" data-testid="ephemeride-link-button">
              <CalendarDays className="w-4 h-4 mr-1" /> Éphéméride
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/contes")} className="rounded-lg" data-testid="family-stories-link-button">
              <BookOpen className="w-4 h-4 mr-1" /> Contes & Poésie
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/abonnement")} className="rounded-lg" data-testid="family-subscription-link-button">
              <CreditCard className="w-4 h-4 mr-1" /> Mon abonnement
            </Button>
            {!isFamily && (
              <Button variant="outline" size="sm" onClick={() => navigate("/espaces")} className="rounded-lg" data-testid="switch-space-button">
                <LayoutGrid className="w-4 h-4 mr-1" /> Changer d'espace
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/connexion"); }} data-testid="family-logout">
              <LogOut className="w-4 h-4 mr-1" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {isFamily ? <FamilyRelativeView /> : <CaregiverFamilyView />}
      </main>
    </div>
  );
}
